import { useCallback, useEffect } from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { countries } from './countries';
import { type RegistrationFormData } from './schema';
import { MaskedInput } from '@/components/shared/masked-input';
import { states } from '@/data/@consts/states';
import { usePostalAddress } from '@/data/postal-address';
import { extractNumbers } from '@/utils/extract-numbers';

const LoadingAdornment = () => (
  <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
);

export const UserDataForm = () => {
  const { getAddressByCep, isPending } = usePostalAddress();
  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<RegistrationFormData>();

  const stateValue = watch('state');
  const cepValue = watch('cep');
  const noNumberValue = watch('no_number');

  const stateOptions = states.map((state) => state.acronym);
  const citiesByState = states.reduce(
    (acc, state) => {
      acc[state.acronym] = state.cities;
      return acc;
    },
    {} as Record<string, string[]>
  );

  const getStateCode = useCallback((value: string): string => {
    if (!value) return '';
    if (value.length === 2) return value.toUpperCase();

    const state = states.find(
      (s) => s.name.toLowerCase() === value.toLowerCase()
    );
    return state ? state.acronym : value;
  }, []);

  const handleCepBlur = useCallback(
    async (cep: string) => {
      const cleanedCep = extractNumbers(cep);
      if (cleanedCep.length !== 8) return;

      getAddressByCep(
        { cep: cleanedCep },
        {
          onSuccess: (data) => {
            setValue('street', data.logradouro || '', { shouldValidate: true });
            setValue('neighborhood', data.bairro || '', {
              shouldValidate: true,
            });
            setValue('city', data.localidade || '', { shouldValidate: true });
            setValue('state', data.uf || '', { shouldValidate: true });
          },
        }
      );
    },
    [getAddressByCep, setValue]
  );

  useEffect(() => {
    if (cepValue) {
      const cleanedCep = extractNumbers(cepValue);

      if (cleanedCep.length === 8) {
        const timer = setTimeout(() => {
          handleCepBlur(cepValue);
        }, 300);

        return () => clearTimeout(timer);
      }
    }
  }, [cepValue, handleCepBlur]);

  return (
    <Box
      sx={{ '& .MuiInputBase-root': { backgroundColor: 'background.paper' } }}
    >
      <Typography variant="h1" color="primary.dark" mb={4}>
        Identificação
      </Typography>
      <Stack gap={2}>
        <Typography variant="h2">Informações pessoais</Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <Box>
                  <FormLabel id="name-label" htmlFor="name">
                    Nome completo (sem abreviações)
                  </FormLabel>
                  <TextField
                    {...field}
                    id="name"
                    placeholder="Nome completo"
                    fullWidth
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    aria-labelledby="name-label"
                  />
                </Box>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 3 }}>
            <Controller
              name="cpf"
              control={control}
              render={({ field }) => (
                <Box flex={1}>
                  <FormLabel id="cpf-label" htmlFor="cpf">
                    CPF
                  </FormLabel>
                  <MaskedInput
                    {...field}
                    id="cpf"
                    fullWidth
                    error={!!errors.cpf}
                    helperText={errors.cpf?.message}
                    mask="000.000.000-00"
                    placeholder="000.000.000-00"
                    aria-labelledby="cpf-label"
                  />
                </Box>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 3 }}>
            <Controller
              name="birthDate"
              control={control}
              render={({ field }) => (
                <Box flex={1}>
                  <FormLabel id="birthDate-label" htmlFor="birthDate">
                    Data de Nascimento
                  </FormLabel>
                  <MaskedInput
                    {...field}
                    id="birthDate"
                    fullWidth
                    error={!!errors.birthDate}
                    helperText={errors.birthDate?.message}
                    mask="00/00/0000"
                    placeholder="dd/mm/aaaa"
                    aria-labelledby="birthDate-label"
                  />
                </Box>
              )}
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <FormLabel id="phone-label" htmlFor="phone">
              Número de celular (WhatsApp)
            </FormLabel>
            <Stack direction="row" alignItems="start" gap={0.5}>
              <Controller
                name="countryCode"
                control={control}
                render={({ field }) => (
                  <Box>
                    <FormControl fullWidth error={!!errors.countryCode}>
                      <Select
                        {...field}
                        id="countryCode"
                        aria-label="Código do país"
                        displayEmpty
                        renderValue={(value) => {
                          if (!value) return 'Selecione';
                          const country = countries.find(
                            (c) => c.code === value
                          );
                          return country ? country.flag : value;
                        }}
                        style={{ width: 62 }}
                      >
                        {countries.map((country) => (
                          <MenuItem
                            key={country.name + country.code}
                            value={country.code}
                            sx={{ display: 'flex', gap: 1 }}
                          >
                            <Typography>{country.flag}</Typography>
                            <Typography fontWeight="bold">
                              {country.name}
                            </Typography>
                            <Typography>{country.code}</Typography>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}
              />
              <Controller
                name="phone"
                control={control}
                render={({ field: { onChange, value, ...field } }) => (
                  <Box flex={1}>
                    <MaskedInput
                      {...field}
                      id="phone"
                      value={value || ''}
                      onAccept={onChange}
                      fullWidth
                      error={!!errors.phone}
                      helperText={errors.phone?.message}
                      mask={[
                        { mask: '(00) 0000-0000' },
                        { mask: '(00) 00000-0000' },
                      ]}
                      placeholder="(00) 00000-0000"
                      unmask={false}
                      lazy={false}
                      aria-labelledby="phone-label"
                    />
                  </Box>
                )}
              />
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <Box flex={1}>
                  <FormLabel id="email-label" htmlFor="email">
                    Email
                  </FormLabel>
                  <TextField
                    {...field}
                    id="email"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    placeholder="seu@email.com"
                    type="email"
                    aria-labelledby="email-label"
                  />
                </Box>
              )}
            />
          </Grid>
        </Grid>
        <Typography variant="h2" mt={3}>
          Endereço
        </Typography>
        <Controller
          name="cep"
          control={control}
          render={({ field }) => (
            <Box>
              <FormLabel id="cep-label" htmlFor="cep">
                CEP
              </FormLabel>
              <MaskedInput
                {...field}
                id="cep"
                fullWidth
                error={!!errors.cep}
                helperText={errors.cep?.message}
                mask="00000-000"
                placeholder="00000-000"
                onBlur={() => handleCepBlur(field.value)}
                aria-labelledby="cep-label"
              />
            </Box>
          )}
        />
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Controller
              name="state"
              control={control}
              render={({ field: { onChange, value, ...field } }) => {
                const normalizedValue = getStateCode(value || '');

                return (
                  <Box flex={1}>
                    <FormLabel id="state-label" htmlFor="state">
                      Estado
                    </FormLabel>
                    <Autocomplete
                      {...field}
                      options={stateOptions}
                      value={normalizedValue || null}
                      onChange={(_, newValue) => {
                        const code = getStateCode(newValue || '');
                        onChange(code);
                        setValue('city', '');
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          id="state"
                          placeholder="Selecione"
                          error={!!errors.state}
                          helperText={errors.state?.message}
                          aria-labelledby="state-label"
                          slotProps={{
                            input: {
                              ...params.InputProps,
                              endAdornment: isPending ? (
                                <LoadingAdornment />
                              ) : (
                                params.InputProps.endAdornment
                              ),
                            },
                            htmlInput: params.inputProps,
                          }}
                        />
                      )}
                    />
                  </Box>
                );
              }}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Controller
              name="city"
              control={control}
              render={({ field: { onChange, value, ...field } }) => (
                <Box flex={1}>
                  <FormLabel id="city-label" htmlFor="city">
                    Cidade
                  </FormLabel>
                  <Autocomplete
                    {...field}
                    options={citiesByState[stateValue] || []}
                    value={value || null}
                    onChange={(_, newValue) => onChange(newValue || '')}
                    disabled={!stateValue}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        id="city"
                        placeholder="Selecione"
                        error={!!errors.city}
                        helperText={errors.city?.message}
                        aria-labelledby="city-label"
                        slotProps={{
                          input: {
                            ...params.InputProps,
                            endAdornment: isPending ? (
                              <LoadingAdornment />
                            ) : (
                              params.InputProps.endAdornment
                            ),
                          },
                          htmlInput: params.inputProps,
                        }}
                      />
                    )}
                  />
                </Box>
              )}
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Controller
              name="street"
              control={control}
              render={({ field }) => (
                <Box flex={1}>
                  <FormLabel id="street-label" htmlFor="street">
                    Rua/Avenida
                  </FormLabel>
                  <TextField
                    {...field}
                    id="street"
                    fullWidth
                    error={!!errors.street}
                    helperText={errors.street?.message}
                    placeholder="Rua da Educação"
                    aria-labelledby="street-label"
                    slotProps={{
                      input: {
                        endAdornment: isPending && <LoadingAdornment />,
                      },
                    }}
                  />
                </Box>
              )}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Controller
              name="neighborhood"
              control={control}
              render={({ field }) => (
                <Box flex={1}>
                  <FormLabel id="neighborhood-label" htmlFor="neighborhood">
                    Bairro
                  </FormLabel>
                  <TextField
                    {...field}
                    id="neighborhood"
                    fullWidth
                    error={!!errors.neighborhood}
                    helperText={errors.neighborhood?.message}
                    placeholder="Educação"
                    aria-labelledby="neighborhood-label"
                    slotProps={{
                      input: {
                        endAdornment: isPending && <LoadingAdornment />,
                      },
                    }}
                  />
                </Box>
              )}
            />
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Stack direction="row" gap={2} alignItems="start">
              <Controller
                name="number"
                control={control}
                render={({ field }) => (
                  <Box flex={1}>
                    <FormLabel id="number-label" htmlFor="number">
                      Número
                    </FormLabel>
                    <TextField
                      {...field}
                      id="number"
                      fullWidth
                      disabled={noNumberValue}
                      error={!!errors.number}
                      helperText={errors.number?.message}
                      placeholder="77"
                      type="text"
                      aria-labelledby="number-label"
                      slotProps={{
                        htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' },
                      }}
                    />
                  </Box>
                )}
              />
              <Stack mt={2.5}>
                <Controller
                  name="no_number"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      sx={{ width: 'fit-content' }}
                      control={
                        <Checkbox
                          {...field}
                          checked={field.value}
                          onChange={(e) => field.onChange(e.target.checked)}
                        />
                      }
                      label="Sem número"
                    />
                  )}
                />
              </Stack>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }}>
            <Controller
              name="complement"
              control={control}
              render={({ field }) => (
                <Box flex={1}>
                  <FormLabel
                    id="complement-label"
                    htmlFor="complement"
                    sx={{
                      display: 'flex',
                      gap: 0.5,
                      alignItems: 'center',
                      mb: 0,
                    }}
                  >
                    Complemento
                    <Typography variant="body2">opcional</Typography>
                  </FormLabel>
                  <TextField
                    {...field}
                    id="complement"
                    fullWidth
                    error={!!errors.complement}
                    helperText={errors.complement?.message}
                    placeholder="3"
                    aria-labelledby="complement-label"
                  />
                </Box>
              )}
            />
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};
